import { ScenarioStap } from "@/app/pages/beheer/scenarios/types.ts";
import {
    PrognoseType,
    OngewensteGebeurtenis as SpoorwebOngewensteGebeurtenisDto,
    ToegepasteVSMEventTypeToegepasteVSM,
} from "@/shared/types";
import { replaceIsoStrings } from "@/shared/utils/dates.ts";
import { addMinutes, isSameDay, parseISO, subMinutes } from "date-fns";

const uniqueMeldingId = () => Math.floor(1200000 + Math.random() * 100000).toString();
const logistiekeAfhandelingMeldingId = (ogMeldingId: string) => `2${ogMeldingId.substring(1)}`;

function setUniqueMeldingId(og: SpoorwebOngewensteGebeurtenisDto, uniqueMeldingId: string): SpoorwebOngewensteGebeurtenisDto {
    return {
        ...og,
        incident: {
            ...og.incident,
            melding: {
                ...og.incident.melding,
                meldingId: uniqueMeldingId,
            },
        },
    };
}

function adjustTime(original: Date, manipulation?: `now+${number}` | `+${number}` | `-${number}`): string {
    const nowMatches = manipulation?.match(/now\+(\d+)/) ?? null;
    const plusMinMatches = manipulation?.match(/([+-])(\d+)/) ?? null;

    if (nowMatches !== null && !Number.isNaN(nowMatches[1])) {
        const minutesToAdd = Number(nowMatches[1]);
        return addMinutes(new Date(), minutesToAdd).toISOString();
    } else if (plusMinMatches !== null && !Number.isNaN(plusMinMatches[2])) {
        const operation = plusMinMatches[1] === "+" ? addMinutes : subMinutes;
        const minutesToAdd = Number(plusMinMatches[2]);
        return operation(original, minutesToAdd).toISOString();
    } else {
        const today = new Date();
        original.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());
        return original.toISOString();
    }
}

export function adjustOngewensteGebeurtenisTimesAndMeldingId(
    stap: ScenarioStap,
    prognoses: PrognoseType[] = [],
    meldingId: string = uniqueMeldingId()
): [SpoorwebOngewensteGebeurtenisDto, string] {
    const now = new Date();
    const og = setUniqueMeldingId(stap.og, meldingId);
    const tijdmanipulaties = stap.tijdmanipulaties;
    const laatstePrognose = prognoses.findLast(() => true);
    const ogMeldingId = og.incident.melding.meldingId;
    const logistiekeAfhandelingId = logistiekeAfhandelingMeldingId(ogMeldingId);

    og.volgnummer = stap.volgnummer;
    og.incident.prognoses = { prognose: prognoses };

    // update prognose times for OG if it exists in the step
    if (tijdmanipulaties?.prognose) {
        const prognoseTijdstip = laatstePrognose?.prognosetijdstip ? parseISO(laatstePrognose.prognosetijdstip) : new Date();

        const newPrognose: PrognoseType = {
            ...laatstePrognose,
            prognosetijdstip: adjustTime(prognoseTijdstip, tijdmanipulaties.prognose),
            status: { status: "Prognose update", statustijdstip: now.toISOString(), toelichting: "Prognose update toelichting" },
        };
        og.incident.prognoses.prognose.push(newPrognose);
    }

    // Update logistieke afhandeling (nearly always just one item) to make sure the meldingId matches the logistieke afhandelingId
    const logistiekeAfhandelingen = og.incident.logistiekeAfhandelingen?.logistiekeAfhandeling?.map((logistiekeAfhandeling) => {
        return {
            ...logistiekeAfhandeling,
            meldingId: logistiekeAfhandelingId,
        };
    });
    if (logistiekeAfhandelingen?.length) {
        og.incident.logistiekeAfhandelingen = { logistiekeAfhandeling: logistiekeAfhandelingen };
    }

    // make sure all other dates have today's date (but keep times as is)
    const ogWithUpdatedDates = replaceIsoStrings(og, (date: Date): string => {
        if (!isSameDay(date, now)) {
            date.setFullYear(now.getFullYear());
            date.setMonth(now.getMonth());
            date.setDate(now.getDate());
        }
        return date.toISOString();
    });

    return [ogWithUpdatedDates, logistiekeAfhandelingId];
}

export function adjustTvsmDossierId(
    tvsm: ToegepasteVSMEventTypeToegepasteVSM,
    logistiekeAfhandelingId: string
): ToegepasteVSMEventTypeToegepasteVSM {
    tvsm.ongewensteGebeurtenis.dossierID = logistiekeAfhandelingId;
    return tvsm;
}
